/*
 * HONEYWELL INC.,
 * COPYRIGHT (c) 2022
 * ALL RIGHTS RESERVED
 *
 * This software is a copyrighted work and/or information protected as a trade secret.
 * Legal rights of Honeywell Inc. in this software is distinct from ownership of any medium
 * in which the software is embodied. Copyright or trade secret notices included must be
 * reproduced in any copies authorized by Honeywell Inc.
 *
 */

import React, { useState, useEffect, useContext, useMemo } from 'react';
import { useTranslation } from '@scuf/localization';
import descriptors from '../../../../../locales/en-US/descriptors';
import ConnectionError from '../../../../shared/ErrorComponents/ConnectionError';
import withErrorStatus from '../../../../shared/StatusWrappers/ErrorStatus';
import withLoadingStatus from '../../../../shared/StatusWrappers/LoadingStatus';
import useConnectorAndDynamicProps from '../../../../shared/useConnectorAndDynamicProps/useConnectorAndDynamicProps';
import { useInterval } from '../../../../shared/useInterval/useInterval';
import useMounted from '../../../../shared/useMount/useMount';
import {
  POLLING_SECONDS,
  POLLING_MILLI_SECONDS
} from '../../../../shared/Utils/Constants';
import { isObjectOrArrayAndEmpty } from '../../../../shared/Utils/Utils';
import {
  ISiteCard,
  IEmaasEnergyUsageComponentData,
  IEmaasEnergyUsageHOCProps,
  IEuiSummary,
  IAny,
  IEuiV4
} from '../../../IEnergyManagementProps';
import PortfolioEnergyCard from '../PortfolioEnergyCard/PortfolioEnergyCard';
import EmaasBasicSubscriptionEnergyUsageComponent from '../../../EmaasEnergyUsage/EmaasBasicSubscriptionEnergyUsageComponent/EmaasBasicSubscriptionEnergyUsageComponent';
import {
  EmaasSubscriptionType,
  EnergyUsageDataSourceType,
  EnergyUsageField
} from '../../../../shared/Enums';
import {
  getEnergyEndDateBasedOnSub,
  getEnergyStartDate
} from '../../../../shared/Utils/EnergyUsagesViewDetailsUtils';
import {
  IPollingContext,
  PollingContext
} from '../../../../../context/PollingContext/PollingContext';
import { getIntervalType } from '../../../../shared/Utils/PortfolioEnergy/PortfolioEnergyVDUtils';
import moment from 'moment';
import NoData from '../../../../shared/ErrorComponents/NoData';
import PortfolioEnergyCardSeparate from '../PortfolioEnergyCard/PortfolioEnergyCardSeparate';
import { ApiContext } from '../../../../../context/ApiContext';
import BreakdownByMeter from '../../../../shared/BreakdownByMeter/BreakdownByMeter';
import { Card } from '@scuf/common';

interface IPortfolioEnergyHOCContentSeparate extends ISiteCard {
  data: IEmaasEnergyUsageComponentData;
  forGraph?: boolean;
  forSummary?: boolean;
  forTable?: boolean;
  onRefresh: () => Promise<void>;
  checkAllSitesAndMonthData?: boolean;
  dataSourceType?: IAny;
}

const PortfolioEnergyHOCContentSeparate = (
  props: IPortfolioEnergyHOCContentSeparate
) => {
  const d = descriptors.energyManagement;
  const [t] = useTranslation();
  const { data, onRefresh, checkAllSitesAndMonthData } = props;

  if (isObjectOrArrayAndEmpty(data)) {
    return (
      <div className="errorDisplay common-error-container">
        <ConnectionError
          title={t(d.CONNECTIONERRORTITLE)}
          message={t(d.CONNECTIONERROR)}
          onRefresh={onRefresh}
        />
      </div>
    );
  }
  return (
    <div>
      <PortfolioEnergyCardSeparate
        forSummary={props.forSummary}
        forGraph={props.forGraph}
        forTable={props.forTable}
        data={data}
        viewType={EnergyUsageField.PORTFOLIOENERGY}
        checkAllSitesAndMonthData={checkAllSitesAndMonthData}
        dataSourceType={props.dataSourceType}
      />
    </div>
  );
};

const PortfolioEnergyHOCWithLoader = withLoadingStatus(
  withErrorStatus(PortfolioEnergyHOCContentSeparate)
);

// Refresh every hour
const ENERGYUSAGE_REFRESH_INTERVAL = POLLING_SECONDS * POLLING_MILLI_SECONDS;

const PortfolioEnergyHOCSeparate = (props: IEmaasEnergyUsageHOCProps) => {
  const [silentLoading, setIsSilentLoading] = useState<boolean>(false);
  const [t] = useTranslation();
  const d = descriptors.energyManagement;
  const [pollingCount, setPollingCount] = useState(0);
  const {
    bearerToken,
    requestContext,
    dataSourceType,
    setDataSourceType
  } = props;

  const apiContext = useContext(ApiContext);

  const isSitePremium =
    requestContext.context.subscriptionType ===
      EmaasSubscriptionType.PREMIUM_SUBSCRIPTION &&
    apiContext.toggleSeparateApi &&
    dataSourceType !== EnergyUsageDataSourceType.UTILITY_METER;

  const [isOutOfRange, setIsOutOfRange] = useState<boolean>(false);

  const startDate = getEnergyStartDate(
    requestContext.context.startTime!,
    requestContext.context.subscriptionType!
  );
  const endDate = getEnergyEndDateBasedOnSub(
    requestContext.context.subscriptionType!,
    requestContext.context.endTime!,
    requestContext.context.startTime!
  );

  const isDateRangeValid = () => {
    const sDate = moment(
      getEnergyStartDate(
        requestContext.context.startTime!,
        requestContext.context.subscriptionType!
      ),
      'YYYY-MM-DD'
    );
    const eDate = moment(
      getEnergyEndDateBasedOnSub(
        requestContext.context.subscriptionType!,
        requestContext.context.endTime!,
        requestContext.context.startTime!
      ),
      'YYYY-MM-DD'
    );
    return eDate.diff(sDate, 'days') <= 365;
  };

  const connectorConfig = {
    name: isSitePremium ? 'emaas-energy-usage-summary' : 'emaas-energy-usage',
    options: {
      startDate: startDate,
      endDate: endDate,
      allDetails: 'true',
      intervalType: getIntervalType(startDate, endDate),
      dataSource: dataSourceType || ''
    }
  };

  const connectorConfigEui = {
    name: 'portfolio-energy-usage-eui',
    options: {
      currentDate: moment(new Date())
        .subtract(1, 'months')
        .startOf('month')
        .format('YYYY-MM-DD')
    }
  };

  const connectorConfigPerformanceAggregation = {
    name: 'emaas-energy-usage-performance-aggregation',
    options: {
      startDate: startDate,
      endDate: endDate,
      allDetails: 'true',
      intervalType: getIntervalType(startDate, endDate),
      dataSource: dataSourceType || ''
    }
  };

  const connectorConfigTable = {
    name: 'emaas-energy-usage-table',
    options: {
      startDate: startDate,
      endDate: endDate,
      allDetails: 'true',
      groupType: 'Site',
      intervalType: getIntervalType(startDate, endDate),
      dataSource: dataSourceType || ''
    }
  };

  const isMounted = useMounted();

  const [dataConnector] = useConnectorAndDynamicProps({
    connectorConfig,
    requestContext,
    bearerToken
  });

  const dataConnectorEui = useConnectorAndDynamicProps({
    connectorConfig: connectorConfigEui,
    requestContext,
    bearerToken
  })[0];

  let dataConnectorPerformanceAggregation: any;
  //if (isSitePremium) {
  dataConnectorPerformanceAggregation = useConnectorAndDynamicProps({
    connectorConfig: connectorConfigPerformanceAggregation,
    requestContext,
    bearerToken
  })[0];
  //}

  let dataConnectorTable: any;
  //if (isSitePremium) {
  dataConnectorTable = useConnectorAndDynamicProps({
    connectorConfig: connectorConfigTable,
    requestContext,
    bearerToken
  })[0];
  //}

  useEffect(() => {
    if (isMounted) {
      onRefresh();
      onRefreshEui();
      onRefreshPerf();
      onRefreshTable();
    }
  }, [pollingCount]);

  useEffect(() => {
    setIsSilentLoading(false);
    if (isMounted) {
      if (isDateRangeValid()) {
        setIsOutOfRange(false);
        onRefresh();
        onRefreshEui();
        onRefreshPerf();
        onRefreshTable();
      } else {
        setIsOutOfRange(true);
      }
    }
  }, [requestContext]);

  useEffect(() => {
    if (!isDateRangeValid()) {
      setIsOutOfRange(true);
    }
  }, []);

  const allEnergy = dataConnector.data;

  const EmaasEnergyUsage = (allEnergy as unknown) as IEmaasEnergyUsageComponentData;

  // Polling mechanism
  useInterval(() => {
    if (
      EmaasEnergyUsage &&
      EmaasEnergyUsage.subscriptionType === EnergyUsageField.PREMIUMSUBS
    ) {
      //setIsSilentLoading(true);
      setPollingCount(pollingCount + 1);
    }
  }, ENERGYUSAGE_REFRESH_INTERVAL);

  const pollingContext = useContext(PollingContext);

  const onRefreshPerf = () =>
    dataConnectorPerformanceAggregation &&
    dataConnectorPerformanceAggregation.actions &&
    dataConnectorPerformanceAggregation.actions.getAll();

  const onRefresh = () =>
    dataConnector && dataConnector.actions && dataConnector.actions.getAll();

  const onRefreshEui = () =>
    dataConnectorEui &&
    dataConnectorEui.actions &&
    dataConnectorEui.actions.getAll();

  const onRefreshTable = () =>
    dataConnectorTable &&
    dataConnectorTable.actions &&
    dataConnectorTable.actions.getAll();

  const allPerf = dataConnectorPerformanceAggregation.data.all;
  const loadingPerf = dataConnectorPerformanceAggregation.data.loading;
  const errorPerf = dataConnectorPerformanceAggregation.data.error;
  const statusPerf = dataConnectorPerformanceAggregation.data.status;

  const allTable = dataConnectorTable.data.all;
  const loadingDataTable = dataConnectorTable.data.loading;
  const errorDataTable = dataConnectorTable.data.error;
  const statusDataTable = dataConnectorTable.data.status;

  const PerformanceData = (allPerf as unknown) as IEmaasEnergyUsageComponentData;
  const isDataAvailableForPerfromance =
    PerformanceData && PerformanceData.performanceAggregation ? true : false;

  const TableData = (allTable as unknown) as IEmaasEnergyUsageComponentData;
  const isDataAvailableForTable =
    TableData && TableData.breakdownBySite ? true : false;

  const { all, loading, error, status } = dataConnector.data;

  const allEui = dataConnectorEui.data.all;
  const loadingEui = dataConnectorEui.data.loading;
  const errorEui = dataConnectorEui.data.error;
  const statusEui = dataConnectorEui.data.status;

  const electricitySavings = (all as unknown) as IEmaasEnergyUsageComponentData;
  const EmaasEnergyUsageEui = (allEui as unknown) as IEuiV4;

  const isDataAvailable =
    electricitySavings && electricitySavings.summary ? true : false;

  const isDataAvailableEui = EmaasEnergyUsageEui ? true : false;

  const [isLoading, setIsLoading] = useState<boolean>(loading);
  const [isLoadingPerf, setIsLoadingPerf] = useState<boolean>(loadingPerf);
  const [statusCode, setStatusCode] = useState<number | null>(0);
  const [errorInfo, setErrorInfo] = useState(errorPerf);
  const [statusCodePerf, setStatusCodePerf] = useState<number | null>(0);
  const [errorInfoPerf, setErrorInfoPerf] = useState(errorPerf);
  const [data, setData] = useState({} as IEmaasEnergyUsageComponentData);
  const [dataPerf, setDataPerf] = useState(
    {} as IEmaasEnergyUsageComponentData
  );

  const [isLoadingEui, setIsLoadingEui] = useState<boolean>(false);
  const [statusCodeEui, setStatusCodeEui] = useState<number | null>(null);
  const [errorInfoEui, setErrorInfoEui] = useState(errorEui);

  const [tableData, setTableData] = useState(
    {} as IEmaasEnergyUsageComponentData
  );
  const [loadingTable, setLoadingTable] = useState<boolean>(loadingDataTable);
  const [errorTable, setErrorTable] = useState(errorDataTable);
  const [statusCodeTable, setStatusCodeTable] = useState<number | null>(0);
  const [checkAllSitesAndMonthData, setCheckAllSitesAndMonthData] = useState<
    boolean
  >(true);

  const checkIfAllSitesHasCompleteData = (data: any) => {
    return (
      data &&
      data.breakdownBySite &&
      (data.subscriptionType !== EnergyUsageField.BASICSUBS ||
        data.breakdownBySite.every(
          (item: { isCompleted: boolean }) => item.isCompleted
        ))
    );
  };

  const checkIfAllMonthHasCompleteData = (data: any) => {
    return (
      data &&
      data.monthWiseAggregations &&
      (data.subscriptionType !== EnergyUsageField.BASICSUBS ||
        data.monthWiseAggregations.every(
          (item: { isCompleted: boolean }) => item.isCompleted
        ))
    );
  };

  const checkIfAllMonthsHasPreviousPeriod = (data: any) => {
    return (
      data &&
      data.monthWiseAggregations &&
      data.monthWiseAggregations.every(
        (item: {
          totalBaselineConsumption: number | null;
          totalConsumption: number | null;
        }) =>
          item.totalBaselineConsumption !== 0 &&
          item.totalBaselineConsumption !== null &&
          item.totalConsumption !== 0 &&
          item.totalConsumption !== null
      )
    );
  };

  const checkIfAllSitesHasPreviousPeriod = (data: any) => {
    return (
      data &&
      data.breakdownBySite &&
      data.breakdownBySite.every(
        (item: {
          totalBaselineConsumption: number | null;
          totalConsumption: number | null;
        }) =>
          item.totalConsumption !== 0 &&
          item.totalBaselineConsumption !== 0 &&
          item.totalConsumption !== null &&
          item.totalBaselineConsumption !== null
      )
    );
  };

  useEffect(() => {
    setIsLoadingPerf(loadingPerf);
    if (
      isDataAvailableForPerfromance ||
      errorPerf ||
      statusPerf ||
      !loadingPerf
    ) {
      if (isDataAvailableForPerfromance) {
        let tempData: any = PerformanceData;
        setDataPerf(tempData);
      }
      setErrorInfoPerf(errorPerf);
      setStatusCodePerf(statusPerf);
    }
  }, [isDataAvailableForPerfromance, loadingPerf, statusPerf]);

  useEffect(() => {
    setIsLoading(loading);

    if (isDataAvailable || error || status || !loading) {
      if (isDataAvailable) {
        let tempData = electricitySavings;
        if (
          (dataSourceType &&
            dataSourceType === EnergyUsageDataSourceType.UTILITY_METER) ||
          !dataSourceType
        ) {
          setCheckAllSitesAndMonthData(
            checkIfAllSitesHasPreviousPeriod(tempData) &&
              checkIfAllMonthsHasPreviousPeriod(tempData) &&
              checkIfAllMonthHasCompleteData(tempData) &&
              checkIfAllSitesHasCompleteData(tempData)
          );
        } else {
          setCheckAllSitesAndMonthData(true);
        }
        setData(tempData);
        // Disable EUI update for live meter dropdown
      }
      setErrorInfo(error);
      setStatusCode(status);
    }
  }, [isDataAvailable, loading, status]);

  useEffect(() => {
    setIsLoadingEui(loadingEui);
    if ((isDataAvailableEui || errorEui || statusEui) && !loadingEui) {
      if (isDataAvailableEui) {
        setContextData(pollingContext, EmaasEnergyUsageEui);
      }
      setErrorInfoEui(errorEui);
    }
    setStatusCodeEui(statusEui);
  }, [isDataAvailableEui, loadingEui, statusEui]);

  useEffect(() => {
    setLoadingTable(loadingDataTable);

    if (
      isDataAvailableForTable ||
      errorDataTable ||
      statusDataTable ||
      !loadingDataTable
    ) {
      if (isDataAvailableForTable) {
        let tempData: any = TableData;
        setTableData(tempData);
      }
      setErrorTable(errorDataTable);
      setStatusCodeTable(statusDataTable);
    }
  }, [isDataAvailableForTable, loadingDataTable, statusDataTable]);

  const [currentTab, setCurrentTab] = useState<string>('All');

  const { enableConsumptionSourceSelect } = apiContext;

  useEffect(() => {
    if (isMounted) {
      onRefresh();
      onRefreshPerf();
      onRefreshTable();
    }
  }, [currentTab, dataSourceType]);

  useEffect(() => {
    if (enableConsumptionSourceSelect) {
      if (currentTab === 'water') {
        setDataSourceType &&
          setDataSourceType(EnergyUsageDataSourceType.LIVE_METER);
      }
    }
  }, [enableConsumptionSourceSelect, currentTab]);

  return isOutOfRange ? (
    <div className="noDataDisplay common-error-container">
      <NoData message={t(d.UTILITYYEAROUTOFRANGE)} />
    </div>
  ) : (
    <>
      {statusCode == 204 || statusCodePerf == 204 || statusCodeTable == 204 ? (
        <PortfolioEnergyHOCWithLoader
          loading={isLoading}
          error={errorInfo}
          statusCode={204}
          onRefresh={onRefresh}
          data={data}
          loadingText={t(d.LOADING)}
          title={t(d.CONNECTIONERRORTITLE)}
          noDataMessage={t(d.NODATAAVAILABLE)}
          connectionErrorMessage={t(d.CONNECTIONERROR)}
          silentLoading={silentLoading}
          forSummary={false}
          checkAllSitesAndMonthData={checkAllSitesAndMonthData}
        />
      ) : (
        <>
          <Card.Content className="kpi-content-container">
            <PortfolioEnergyHOCWithLoader
              loading={isLoading}
              error={errorInfo}
              statusCode={statusCode}
              onRefresh={onRefresh}
              data={data}
              loadingText={t(d.LOADING)}
              title={t(d.CONNECTIONERRORTITLE)}
              noDataMessage={t(d.NODATAAVAILABLE)}
              connectionErrorMessage={t(d.CONNECTIONERROR)}
              silentLoading={silentLoading}
              forSummary={true}
              checkAllSitesAndMonthData={checkAllSitesAndMonthData}
            />
          </Card.Content>
          <Card.Content className="kpi-content-container">
            <PortfolioEnergyHOCWithLoader
              loading={isLoadingPerf}
              error={errorInfoPerf}
              statusCode={statusCodePerf}
              onRefresh={onRefreshPerf}
              data={dataPerf}
              loadingText={t(d.LOADING)}
              title={t(d.CONNECTIONERRORTITLE)}
              noDataMessage={t(d.NODATAAVAILABLE)}
              connectionErrorMessage={t(d.CONNECTIONERROR)}
              silentLoading={silentLoading}
              forGraph={true}
              checkAllSitesAndMonthData={checkAllSitesAndMonthData}
              dataSourceType={dataSourceType}
            />
          </Card.Content>
          <Card.Content className="kpi-content-container">
            <PortfolioEnergyHOCWithLoader
              loading={loadingTable}
              error={errorTable}
              statusCode={statusCodeTable}
              onRefresh={onRefreshTable}
              data={tableData}
              loadingText={t(d.LOADING)}
              title={t(d.CONNECTIONERRORTITLE)}
              noDataMessage={t(d.NODATAAVAILABLE)}
              connectionErrorMessage={t(d.CONNECTIONERROR)}
              silentLoading={silentLoading}
              forTable={true}
              checkAllSitesAndMonthData={checkAllSitesAndMonthData}
            />
          </Card.Content>
        </>
      )}
    </>
  );
};

const setContextData = (
  pollingContext: IPollingContext,
  EmaasEnergyUsage: IEuiV4
) => {
  const { setPollingTime, setEuiSummary } = pollingContext;

  setPollingTime && setPollingTime();
  setEuiSummary &&
    setEuiSummary(
      (EmaasEnergyUsage &&
        EmaasEnergyUsage.data &&
        EmaasEnergyUsage.data.euiSummary) ||
        ({} as IEuiSummary)
    );
};

export default PortfolioEnergyHOCSeparate;
